import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Send, CheckCircle2 } from 'lucide-react';
import { submitContact } from '../../lib/api';
import { withTimeout } from '../../lib/utils';
import { Input } from '../ui/input';

const SUBMIT_TIMEOUT_MS = 4000;
const TEAL = '#62CED3';

export default function GetInTouchModal({ onClose }) {
  const [form, setForm] = useState({ name: '', email: '', phone: '', course: '' });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  const handleBackdrop = (e) => { if (e.target === e.currentTarget) onClose(); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const contactPromise = submitContact({
      ...form,
      message: `Get in Touch enquiry${form.course ? ` — interested course: ${form.course}` : ''}`,
      source: 'navbar_get_in_touch',
      page: 'navbar_get_in_touch',
    });

    const result = await withTimeout(contactPromise, SUBMIT_TIMEOUT_MS);
    setLoading(false);
    if (!result.timedOut && result.error) {
      setError('Something went wrong. Please try again.');
      return;
    }
    setSubmitted(true);
  };

  // Portal to <body>: the navbar's backdrop-blur creates a containing block
  // for fixed descendants, which would trap this overlay inside the nav bar.
  return createPortal(
    <div
      data-testid="get-in-touch-modal"
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-[#0F1F3D]/70 backdrop-blur-sm"
      onClick={handleBackdrop}
    >
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-5 border-b border-[#E1E7EF]">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider mb-0.5" style={{ color: TEAL }}>
              A7 Globetech
            </p>
            <h2 className="font-heading font-bold text-lg text-[#1F1F1F]">
              Get in Touch with us
            </h2>
          </div>
          <button
            onClick={onClose}
            data-testid="get-in-touch-modal-close"
            className="w-8 h-8 rounded-full bg-[#F0F0F0] hover:bg-[#E0E0E0] flex items-center justify-center transition-colors"
          >
            <X className="w-4 h-4 text-[#585858]" />
          </button>
        </div>

        {submitted ? (
          <div className="p-8 text-center">
            <div className="w-14 h-14 rounded-full flex items-center justify-center mb-4 mx-auto" style={{ backgroundColor: '#E4F8F9' }}>
              <CheckCircle2 className="w-7 h-7" style={{ color: TEAL }} />
            </div>
            <h3 className="font-heading font-bold text-lg text-[#1F1F1F]">
              Thanks{form.name ? `, ${form.name}` : ''}!
            </h3>
            <p className="font-body text-sm text-[#585858] mt-2">
              Our counsellor will reach out to you shortly.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            <p className="text-sm text-[#585858]">
              Share your details and our counsellor will reach out to you shortly.
            </p>

            <div>
              <label className="text-xs font-medium text-[#1F1F1F] mb-1 block">Full Name *</label>
              <Input
                data-testid="get-in-touch-name-input"
                value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })}
                required
                placeholder="Your full name"
                className="h-11 border-[#E1E7EF] focus:border-[#0056D2] rounded-md"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-[#1F1F1F] mb-1 block">Email *</label>
              <Input
                data-testid="get-in-touch-email-input"
                type="email"
                value={form.email}
                onChange={e => setForm({ ...form, email: e.target.value })}
                required
                placeholder="your@email.com"
                className="h-11 border-[#E1E7EF] focus:border-[#0056D2] rounded-md"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-[#1F1F1F] mb-1 block">Phone Number *</label>
              <Input
                data-testid="get-in-touch-phone-input"
                type="tel"
                value={form.phone}
                onChange={e => setForm({ ...form, phone: e.target.value })}
                required
                placeholder="+91 XXXXX XXXXX"
                className="h-11 border-[#E1E7EF] focus:border-[#0056D2] rounded-md"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-[#1F1F1F] mb-1 block">Which course are you interested in?</label>
              <Input
                data-testid="get-in-touch-course-input"
                value={form.course}
                onChange={e => setForm({ ...form, course: e.target.value })}
                placeholder="e.g. Data Science & Gen AI"
                className="h-11 border-[#E1E7EF] focus:border-[#0056D2] rounded-md"
              />
            </div>

            {error && <p className="text-xs text-red-600">{error}</p>}

            <button
              type="submit"
              disabled={loading}
              data-testid="get-in-touch-submit-btn"
              className="w-full text-sm font-semibold py-3 rounded-md text-white flex items-center justify-center gap-2 transition-opacity hover:opacity-90 disabled:opacity-60"
              style={{ backgroundColor: TEAL }}
            >
              <Send className="w-4 h-4" />
              {loading ? 'Submitting...' : 'Request Callback'}
            </button>
            <p className="text-[11px] text-center text-[#9AA5B5]">
              By submitting, you agree to be contacted by our team.
            </p>
          </form>
        )}
      </div>
    </div>,
    document.body
  );
}
